/* tslint:disable:no-shadowed-variable */
import camelcaseKeysDeep from 'camelcase-keys-deep'
import { ModuleNames } from '@/store/module-names'
import { ExerciseState, LoadingTypes } from './types'
import { ActionNames, MutationNames } from './keys'
import { getters } from './getters'
import {
  deleteExerciseQuery,
  getExercisesQuery,
  getExercisesQueryByPatient,
  patchExerciseQuery,
  PatientExerciseSchema,
  postExerciseQuery,
} from '@/jsonapi/PatientExercise'
import { MAX_RETRY_COUNT, RETRY_INTERVAL } from '@/constants/api-config'
import { timeout } from '@/utils/time'

const state: ExerciseState = {
  exercises: [],
  autosaveTimer: null,
  loading: LoadingTypes.INITIAL,
}

const mutations = {
  [MutationNames.updateExercises](state, payload: PatientExerciseSchema[]): void {
    state.exercises = payload
  },
  [MutationNames.updateExercise](state, payload: PatientExerciseSchema): void {
    const id = payload.id
    const targetIndex = state.exercises.findIndex(exercise => exercise.id === id)
    if (targetIndex !== -1) {
      state.exercises.splice(targetIndex, 1, payload)
    } else {
      state.exercises.push(payload)
    }
  },
  [MutationNames.addExercise](state, payload: PatientExerciseSchema): void {
    state.exercises.push(payload)
  },
  [MutationNames.setTimer](state, timer): void {
    state.timer = timer
  },
  [MutationNames.clearTimer](state): void {
    window.clearTimeout(state.timer)
  },
  [MutationNames.setLoading](state, payload): void {
    state.loading = payload
  },
}

const actions = {
  async [ActionNames.getExercises]({ commit }): Promise<void> {
    commit(MutationNames.setLoading, LoadingTypes.FULL_SCREEN)
    const exercises = camelcaseKeysDeep(await getExercisesQuery())
    commit(MutationNames.updateExercises, exercises)
    commit(MutationNames.setLoading, LoadingTypes.INITIAL)
    return exercises
  },
  async [ActionNames.getExercisesByPatient]({ state, commit }, patientId: string): Promise<void> {
    commit(MutationNames.setLoading, LoadingTypes.FULL_SCREEN)
    const exercises = camelcaseKeysDeep(await getExercisesQueryByPatient(patientId))
    commit(MutationNames.updateExercises, [
      ...state.exercises.filter(ex => ex.attributes.patientId !== patientId),
      ...exercises,
    ])
    commit(MutationNames.setLoading, LoadingTypes.INITIAL)
    return exercises
  },
  async [ActionNames.updateExercise]({ commit, dispatch }, payload: PatientExerciseSchema): Promise<void> {
    commit(MutationNames.updateExercise, payload)
    commit(MutationNames.clearTimer)
    commit(
      MutationNames.setTimer,
      window.setTimeout(() => {
        dispatch(ActionNames.patchExercise, payload.id)
      }, 750)
    )
  },
  async [ActionNames.postExercise]({ commit }, payload: { patientId: string }): Promise<string> {
    commit(MutationNames.setLoading, LoadingTypes.FULL_SCREEN)
    const newExerciseId = await postExerciseQuery(payload)
    commit(MutationNames.setLoading, LoadingTypes.INITIAL)
    return newExerciseId
  },

  /*
  async [ActionNames.patchExercise]({ state, commit }, exerciseId): Promise<void> {
    commit(MutationNames.setLoading, LoadingTypes.PARTIAL)
    const exercise = state.exercises.find(e => e.id === exerciseId)
    await patchExerciseQuery(exercise)
    commit(MutationNames.setLoading, LoadingTypes.INITIAL)
  },
  */

  async [ActionNames.patchExercise]({ state, commit }, payload): Promise<void> {
    commit(MutationNames.setLoading, LoadingTypes.PARTIAL)
    //const exercise = state.exercises.find(e => e.id === exerciseId)
    await patchExerciseQuery(payload)
    commit(MutationNames.setLoading, LoadingTypes.INITIAL)
  },

  async [ActionNames.deleteExercise](
    { commit, dispatch },
    { exerciseId, patientId }: { exerciseId: string; patientId: string }
  ): Promise<void> {
    await deleteExerciseQuery(exerciseId)

    for (let i = 0; i < MAX_RETRY_COUNT; i++) {
      await timeout(RETRY_INTERVAL)

      const results = patientId
        ? await dispatch(ActionNames.getExercisesByPatient, patientId)
        : await dispatch(ActionNames.getExercises)

      const deletedNote = results.find(result => result.id === exerciseId)

      if (!deletedNote) {
        break
      }
    }
  },
}

export default {
  name: ModuleNames.exercise,
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
